import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Instagram, Linkedin, Twitter, ArrowRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { siteConfig } from '../../data/mock';

export const Footer = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Newsletter Section */}
      <div className="border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="max-w-2xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-white mb-3">Stay Connected</h3>
            <p className="text-slate-400 mb-6">Subscribe to our newsletter for updates on our programs, success stories, and ways to get involved.</p>
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:border-teal-500"
                required
              />
              <Button type="submit" className="bg-teal-600 hover:bg-teal-500 text-white whitespace-nowrap">
                {subscribed ? 'Subscribed!' : 'Subscribe'}
                {!subscribed && <ArrowRight className="w-4 h-4 ml-2" />}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img 
                src="/logo.jpg" 
                alt="Productive Offenders of Society Foundation" 
                className="h-14 w-auto rounded-lg"
              />
            </Link>
            <p className="text-slate-400 text-sm mb-4">Empowering second chances and building brighter futures since 2023.</p>
            <div className="flex gap-3">
              <a href={siteConfig.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-teal-600 hover:text-white transition-all duration-200">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={siteConfig.socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-teal-600 hover:text-white transition-all duration-200">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={siteConfig.socialLinks.linkedin} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-teal-600 hover:text-white transition-all duration-200">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href={siteConfig.socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-9 h-9 bg-slate-800 rounded-lg flex items-center justify-center text-slate-400 hover:bg-teal-600 hover:text-white transition-all duration-200">
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/about" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">About Us</Link></li>
              <li><Link to="/reentry-support" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Reentry Support</Link></li>
              <li><Link to="/resources" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Resources</Link></li>
              <li><Link to="/gallery" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Gallery</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Get Involved */}
          <div>
            <h4 className="font-semibold text-white mb-4">Get Involved</h4>
            <ul className="space-y-2">
              <li><Link to="/get-involved" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Volunteer</Link></li>
              <li><Link to="/get-involved#donate" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Donate</Link></li>
              <li><Link to="/get-involved#partner" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Become a Partner</Link></li>
              <li><Link to="/contact" className="text-slate-400 hover:text-teal-400 transition-colors text-sm">Contact Us</Link></li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                <a href={`mailto:${siteConfig.email}`} className="text-slate-400 hover:text-teal-400 transition-colors text-sm">{siteConfig.email}</a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                <a href={`tel:${siteConfig.phone}`} className="text-slate-400 hover:text-teal-400 transition-colors text-sm">{siteConfig.phone}</a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-teal-500 mt-0.5 flex-shrink-0" />
                <span className="text-slate-400 text-sm">{siteConfig.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">© {new Date().getFullYear()} Productive Offenders of Society Foundation. All rights reserved.</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="text-slate-500 hover:text-slate-300 text-sm transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
